import { useState, type KeyboardEvent } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { XMarkIcon } from '@heroicons/react/24/outline';
import LeadCard from './LeadCard';
import { StateColorPickerPopover } from '../domain/StateColorPickerPopover';
import { chipTextColor } from '../../utils/format';
import type { Lead, LeadStage } from '../../services/api/leads';
import { weightedValue } from '../../services/api/leadStages';
import { LEGACY_CZ_NUMBER_LOCALE, numberFormatter } from '../../utils/localeFormat';

interface LeadColumnProps {
  // MKS-345 — the whole configured stage, not a key and a label: the header
  // paints itself from the stage's own colour and weights its total by the
  // stage's win probability.
  stage: LeadStage;
  leads: Lead[];
  // F13-003: per-currency totals so we don't sum CZK and EUR into a single
  // misleading number.
  totalValue: Record<string, number>;
  staleLeadIds: Set<number>;
  dealValueCZKMap: Map<number, number>;
  onCardClick: (lead: Lead) => void;
  // Phase 105 Plan 105-08 — see LeadKanbanBoard.tsx docblock. Defaults to
  // 'internal' so every existing call site is unchanged.
  audience?: 'internal' | 'external';
  /** MKS-347 — an internal viewer who may not write (marketing). See LeadKanbanBoard. */
  readOnly?: boolean;
  /**
   * When false the header is a coloured chip, a count and the raw total.
   * When true it also shows the weighted value, and — if `onRename` and
   * friends are given — becomes editable in place, the same interactions
   * the ticket board's column header has.
   */
  configurable?: boolean;
  onRename?: (stageId: number, name: string) => void;
  onRecolor?: (stageId: number, color: string) => void;
  onDelete?: (stageId: number) => void;
}

function formatCurrency(value: Record<string, number>): string {
  const entries = Object.entries(value).filter(([, n]) => n);
  if (entries.length === 0) return '0 CZK';
  return entries
    .map(([cur, n]) =>
      numberFormatter({
        style: 'currency',
        currency: cur,
        maximumFractionDigits: 0,
      }, LEGACY_CZ_NUMBER_LOCALE).format(n),
    )
    .join(' · ');
}

export default function LeadColumn({
  stage,
  leads,
  totalValue,
  staleLeadIds,
  dealValueCZKMap,
  onCardClick,
  audience = 'internal',
  readOnly = false,
  configurable = false,
  onRename,
  onRecolor,
  onDelete,
}: LeadColumnProps) {
  const isExternal = audience === 'external';
  // MKS-347 — external and read-only internal (marketing) are the same thing
  // as far as dropping on this column goes.
  const canDrop = !isExternal && !readOnly;
  const { setNodeRef, isOver } = useDroppable({ id: stage.id, disabled: !canDrop });
  // Nothing can ever be dropped externally, so the hover-highlight styling
  // (which only makes sense as feedback for an in-progress drag) is dropped
  // too — `isOver` from a disabled droppable is always false anyway, but
  // this makes the intent explicit.
  const showDropHighlight = canDrop && isOver;

  const [isRenaming, setIsRenaming] = useState(false);
  const [isPickingColor, setIsPickingColor] = useState(false);

  // Editing is offered only when the flag is on AND the page handed down the
  // handlers (admin / sales on the internal board). Everyone else — external
  // viewers, marketing, sales while the flag is off — gets a static header.
  const canEdit = configurable && canDrop && Boolean(onRename && onRecolor && onDelete);

  function commitRename(value: string) {
    const trimmed = value.trim();
    if (trimmed && trimmed !== stage.name) onRename?.(stage.id, trimmed);
    setIsRenaming(false);
  }

  function onNameKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsRenaming(false);
    }
  }

  const weighted = weightedValue(totalValue, stage.probability);

  return (
    <div
      ref={setNodeRef}
      data-stage-id={stage.id}
      className={`flex w-72 shrink-0 flex-col rounded-lg border transition-colors ${
        showDropHighlight ? 'border-primary-400 bg-primary-50' : 'border-gray-200 bg-gray-50'
      }`}
    >
      <div className="px-3 py-2 border-b border-gray-200">
        <div className="flex items-center justify-between gap-2">
          <div className="relative flex min-w-0 items-center gap-2">
            {isRenaming ? (
              <input
                type="text"
                autoFocus
                defaultValue={stage.name}
                aria-label="Stage name"
                onBlur={(e) => commitRename(e.currentTarget.value)}
                onKeyDown={onNameKeyDown}
                className="w-32 rounded border border-gray-300 px-1.5 py-0.5 text-xs"
              />
            ) : (
              <span
                // MKS-345 — the stage's own colour, seeded to the hexes behind
                // the Tailwind chip classes this used to hard-code per slug.
                style={{ backgroundColor: stage.color, color: chipTextColor(stage.color) }}
                className={`inline-flex max-w-[9rem] truncate rounded-full px-2 py-0.5 text-xs font-medium ${
                  canEdit ? 'cursor-text' : ''
                }`}
                {...(canEdit
                  ? {
                      role: 'button',
                      tabIndex: 0,
                      title: 'Rename stage',
                      onClick: () => setIsRenaming(true),
                      onKeyDown: (e: KeyboardEvent<HTMLSpanElement>) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setIsRenaming(true);
                        }
                      },
                    }
                  : {})}
              >
                {stage.name}
              </span>
            )}
            <span className="text-xs text-gray-400">{leads.length}</span>

            {canEdit && (
              <>
                <button
                  type="button"
                  aria-label={`Choose colour for ${stage.name}`}
                  className="h-4 w-4 shrink-0 rounded border border-gray-300"
                  style={{ backgroundColor: stage.color }}
                  // WR-01 (StateListEditor.tsx) — stop the mousedown so the
                  // popover's click-outside listener doesn't close it before
                  // this onClick reopens it.
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => setIsPickingColor((prev) => !prev)}
                />
                {isPickingColor && (
                  <StateColorPickerPopover
                    currentColor={stage.color}
                    onPick={(hex) => onRecolor?.(stage.id, hex)}
                    onClose={() => setIsPickingColor(false)}
                  />
                )}
              </>
            )}
          </div>

          <div className="flex items-center gap-1">
            <span className="text-xs font-medium text-gray-600">
              {formatCurrency(totalValue)}
            </span>
            {canEdit && (
              <button
                type="button"
                aria-label={`Delete ${stage.name}`}
                title={
                  leads.length > 0
                    ? 'Move the leads out of this stage before deleting it'
                    : 'Delete stage'
                }
                onClick={() => onDelete?.(stage.id)}
                className="text-gray-400 hover:text-red-600"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* MKS-345 — value × probability, the forecast line the ticket asks
            for. Hidden while the flag is off, so the header is byte-for-byte
            what it was. */}
        {configurable && (
          <div className="mt-0.5 flex items-center justify-between text-[11px] text-gray-400">
            <span>{stage.probability}%</span>
            <span data-testid={`weighted-total-${stage.id}`}>
              {formatCurrency(weighted)} weighted
            </span>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[200px]">
        <SortableContext items={leads.map((l) => l.id)} strategy={verticalListSortingStrategy}>
          {leads.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              isStale={staleLeadIds.has(lead.id)}
              dealValueCZK={dealValueCZKMap.get(lead.id)}
              onClick={onCardClick}
              audience={audience}
              readOnly={readOnly}
            />
          ))}
        </SortableContext>
        {leads.length === 0 && (
          <p className="py-8 text-center text-xs text-gray-400">No leads</p>
        )}
      </div>
    </div>
  );
}
